// ContestantInfoDrawer.jsx
import {
  Avatar,
  Card,
  Col,
  Drawer,
  Flex,
  List,
  Row,
  Space,
  Typography,
  Form,
  Input,
  Button,
  message,
  Checkbox,
  DatePicker,
  Select,
} from "antd";
import dayjs from "dayjs";
import { fetchProfilePhoto, uploadProfilePhoto } from "../../api/user";
import { useEffect, useState, useRef } from "react";
import {
  updateContestantDetails,
  downloadProfilePhoto,
  updateContestantProvince,
} from "../../api/contestant";
import { fetchProvinces } from "../../api/competition";

const { Text, Title } = Typography;
const { Option } = Select;

// now taking currentUser and competitionId as props
const ContestantInfoDrawer = ({
  contestant,
  onClose,
  onDataUpdated,
  currentUser,
  competitionId,
}) => {
  const [profilePhotoUrl, setProfilePhotoUrl] = useState(null);
  const [form] = Form.useForm();
  const fileInputRef = useRef(null);

  const [provinces, setProvinces] = useState([]);
  const [provinceLoading, setProvinceLoading] = useState(false);
  const [provinceSaving, setProvinceSaving] = useState(false);
  const [selectedProvinceId, setSelectedProvinceId] = useState(null);

  const isAdmin = currentUser && currentUser.role === "admin";

  useEffect(() => {
    if (!contestant) return;
    console.log(contestant);
    const fetchData = async () => {
      const photo = await fetchProfilePhoto(contestant._id);
      setProfilePhotoUrl(photo);
      form.setFieldsValue({
        first_name: contestant.first_name,
        last_name: contestant.last_name,
        email: contestant.email,
        gender: contestant.gender,
        cell_number: contestant.cell_number,
        work_number: contestant.work_number,
        id_number: contestant.id_number,
        passport_number: contestant.passport_number,
        date_of_birth: contestant.date_of_birth
          ? dayjs(contestant.date_of_birth)
          : null,
        address: contestant.address?.[0] || {},
        ice_contact_name: contestant.ice_contact_name,
        relationship_to_ice: contestant.relationship_to_ice,
        ice_contact_email: contestant.ice_contact_email,
        allergies:
          contestant.allergies === true ||
          contestant.allergies === "true" ||
          contestant.allergies === "Yes",
        allergies_specification: contestant.allergies_specification,
        academic_institution: contestant.academic_institution,
        provincial_director: contestant.provincial_director,
      });

      if (contestant.province) {
        // province might be populated doc or just id
        const id =
          typeof contestant.province === "object"
            ? contestant.province._id
            : contestant.province;
        setSelectedProvinceId(id || null);
      } else {
        setSelectedProvinceId(null);
      }
    };
    fetchData();
  }, [contestant, form]);

  // Load provinces for admins (per competition)
  useEffect(() => {
    if (!isAdmin || !competitionId || !contestant) return;

    const loadProvinces = async () => {
      try {
        setProvinceLoading(true);
        const data = await fetchProvinces(competitionId);
        setProvinces((data || []).filter((p) => p.active));
      } catch (error) {
        console.error("Error fetching provinces:", error);
        message.error("Could not load provinces for this competition.");
      } finally {
        setProvinceLoading(false);
      }
    };

    loadProvinces();
  }, [isAdmin, competitionId, contestant]);

  const getInitials = (name) => {
    const rgx = new RegExp(/(\p{L}{1})\p{L}+/, "gu");
    let initials = [...name.matchAll(rgx)] || [];
    initials = (initials.shift()?.[1] || "") + (initials.pop()?.[1] || "");
    return initials.toUpperCase();
  };

  const handleDownloadProfilePhoto = async () => {
    try {
      const blob = await downloadProfilePhoto(contestant._id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;

      let extension = "jpg";
      if (blob.type === "image/png") extension = "png";
      else if (blob.type === "image/webp") extension = "webp";

      a.download =
        (contestant.first_name || "profile") +
        "_" +
        (contestant.last_name || "photo") +
        "." +
        extension;

      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      message.error("Could not download profile photo.");
    }
  };

  const handleChangeProfilePhotoClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleProfilePhotoChange = async (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    try {
      await uploadProfilePhoto(contestant._id, file);
      const updatedPhoto = await fetchProfilePhoto(contestant._id);
      setProfilePhotoUrl(updatedPhoto);
      message.success("Profile photo updated successfully!");
    } catch (error) {
      console.error("Error updating profile photo:", error);
      message.error("Failed to update profile photo. Please try again.");
    } finally {
      event.target.value = "";
    }
  };

  const handleUpdate = async (values) => {
    try {
      const response = await updateContestantDetails(contestant._id, {
        ...values,
        address: [values.address],
        date_of_birth: values.date_of_birth
          ? values.date_of_birth.toDate()
          : null,
      });

      // API returns { message, contestant } — normalize to the contestant object
      const updated = response && response.contestant ? response.contestant : response;

      message.success("Contestant details updated successfully!");

      // Refresh form with canonical server data
      form.setFieldsValue({
        ...updated,
        date_of_birth: updated.date_of_birth ? dayjs(updated.date_of_birth) : null,
        address: updated.address?.[0] || {},
      });

      if (onDataUpdated) onDataUpdated(updated);
    } catch (error) {
      console.error("Failed to update contestant details:", error);
      message.error("Failed to update contestant details. Please try again.");
    }
  };

  const handleSaveProvince = async () => {
    if (!selectedProvinceId) {
      message.warning("Please select a province before saving.");
      return;
    }
    try {
      setProvinceSaving(true);
      const updated = await updateContestantProvince(
        contestant._id,
        selectedProvinceId
      );
      message.success(
        "Province updated for contestant and related applications."
      );
      if (onDataUpdated) onDataUpdated(updated);
    } catch (error) {
      console.error("Failed to update province:", error);
      message.error("Failed to update province. Please try again.");
    } finally {
      setProvinceSaving(false);
    }
  };

  return (
    <Drawer
      title="Contestant Information"
      placement="right"
      onClose={onClose}
      open={contestant !== null}
      width="800"
    >
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <Flex align="center">
          <Space>
            <Avatar
              draggable={true}
              size={80}
              style={{ backgroundColor: "#1271dd", fontSize: 35 }}
              src={profilePhotoUrl}
            >
              {contestant &&
                getInitials(
                  `${contestant.first_name} ${contestant.last_name}`
                )}
            </Avatar>
            <Space direction="vertical">
              <Title style={{ margin: 0 }} level={3}>
                {contestant?.first_name} {contestant?.last_name}
              </Title>
              <Text style={{ margin: 0 }}>{contestant?.email}</Text>
              <Text style={{ margin: 0 }}>
                Age: {contestant?.date_of_birth ? dayjs().diff(dayjs(contestant.date_of_birth), "year") : "N/A"}
              </Text>
              <Button
                type="default"
                size="small"
                onClick={handleDownloadProfilePhoto}
                style={{ marginTop: 8 }}
              >
                Download Profile Photo
              </Button>
              <Button
                type="primary"
                size="small"
                onClick={handleChangeProfilePhotoClick}
                style={{ marginTop: 8 }}
              >
                Change Profile Photo
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={handleProfilePhotoChange}
              />
            </Space>
          </Space>
        </Flex>

        {contestant && contestant.province && (
          <Card type="inner" title="Province Information" size="small">
            <List>
              <List.Item>
                <Text strong>Province: </Text>
                <Text>
                  {typeof contestant.province === "object"
                    ? contestant.province.province
                    : ""}
                </Text>
              </List.Item>
              <List.Item>
                <Text strong>Location: </Text>
                <Text>
                  {typeof contestant.province === "object"
                    ? contestant.province.location
                    : ""}
                </Text>
              </List.Item>
              <List.Item>
                <Text strong>Date: </Text>
                <Text>
                  {typeof contestant.province === "object" &&
                  contestant.province.date
                    ? dayjs(contestant.province.date).format("YYYY-MM-DD")
                    : ""}
                </Text>
              </List.Item>

              {isAdmin && (
                <>
                  <List.Item>
                    <Text strong>Change Province: </Text>
                    <Select
                      style={{ width: "100%" }}
                      placeholder="Select province"
                      value={selectedProvinceId || undefined}
                      onChange={(value) => setSelectedProvinceId(value)}
                      loading={provinceLoading}
                    >
                      {provinces.map((p) => (
                        <Option key={p._id} value={p._id}>
                          {p.province} {p.location ? `- ${p.location}` : ""}
                        </Option>
                      ))}
                    </Select>
                  </List.Item>
                  <List.Item>
                    <Button
                      type="primary"
                      onClick={handleSaveProvince}
                      loading={provinceSaving}
                    >
                      Save Province
                    </Button>
                  </List.Item>
                </>
              )}
            </List>
          </Card>
        )}

        {contestant && !contestant.province && isAdmin && (
          <Card type="inner" title="Province Information" size="small">
            <List>
              <List.Item>
                <Text strong>Change Province: </Text>
                <Select
                  style={{ width: "100%" }}
                  placeholder="Select province"
                  value={selectedProvinceId || undefined}
                  onChange={(value) => setSelectedProvinceId(value)}
                  loading={provinceLoading}
                >
                  {provinces.map((p) => (
                    <Option key={p._id} value={p._id}>
                      {p.province} {p.location ? `- ${p.location}` : ""}
                    </Option>
                  ))}
                </Select>
              </List.Item>
              <List.Item>
                <Button
                  type="primary"
                  onClick={handleSaveProvince}
                  loading={provinceSaving}
                >
                  Save Province
                </Button>
              </List.Item>
            </List>
          </Card>
        )}

        {contestant && contestant.guardian && (
          <Card type="inner" title="Guardian Information" size="small">
            <List>
              <List.Item>
                <Text strong>Name: </Text>
                <Text>
                  {contestant.guardian.first_name}{" "}
                  {contestant.guardian.last_name}
                </Text>
              </List.Item>
              <List.Item>
                <Text strong>Email: </Text>
                <Text>{contestant.guardian.email}</Text>
              </List.Item>
              <List.Item>
                <Text strong>Cell Number: </Text>
                <Text>
                  {contestant.guardian.cell_number || "Not provided"}
                </Text>
              </List.Item>
            </List>
          </Card>
        )}

        <Form form={form} layout="vertical" onFinish={handleUpdate}>
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Card type="inner" title="Contestant Information" size="small">
                <Form.Item
                  name="first_name"
                  label="First Name"
                  rules={[{ required: true, message: "Please enter first name" }]}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  name="last_name"
                  label="Last Name"
                  rules={[{ required: true, message: "Please enter last name" }]}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  name="email"
                  label="Email"
                  rules={[
                    { required: true, message: "Please enter email" },
                    { type: "email", message: "Please enter a valid email" },
                  ]}
                >
                  <Input />
                </Form.Item>
                <Form.Item name="gender" label="Gender">
                  <Input />
                </Form.Item>
                <Form.Item
                  name="cell_number"
                  label="Cell Number"
                  rules={[
                    { required: true, message: "Please input your cell number!" },
                    {
                      pattern: /^[0-9]{10}$/,
                      message: "Please input a valid cell number!",
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  name="work_number"
                  label="Work Number"
                  rules={[
                    {
                      pattern: /^[0-9]{10}$/,
                      message: "Please input a valid cell number!",
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  name="date_of_birth"
                  label="Date of Birth"
                  rules={[
                    { required: true, message: "Please input date of birth!" },
                  ]}
                >
                  <DatePicker
                    format="YYYY-MM-DD"
                    style={{ width: "100%" }}
                    disabledDate={(d) => d && d > dayjs().endOf("day")}
                  />
                </Form.Item>
                <Form.Item
                  name="id_number"
                  label="ID Number"
                  rules={[
                    {
                      required: true,
                      message:
                        "Please input your ID number! Enter 0 if not applicable",
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  name="passport_number"
                  label="Passport Number"
                  rules={[
                    {
                      required: true,
                      message:
                        "Please input your passport number! Enter 0 if not applicable",
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Card>
            </Col>
            <Col span={12}>
              <Card type="inner" title="Address" size="small">
                <Form.Item name={["address", "street_1"]} label="Street 1">
                  <Input />
                </Form.Item>
                <Form.Item name={["address", "street_2"]} label="Street 2">
                  <Input />
                </Form.Item>
                <Form.Item name={["address", "street_3"]} label="Street 3">
                  <Input />
                </Form.Item>
                <Form.Item name={["address", "city"]} label="City">
                  <Input />
                </Form.Item>
                <Form.Item name={["address", "province"]} label="Province">
                  <Input />
                </Form.Item>
                <Form.Item name={["address", "country"]} label="Country">
                  <Input />
                </Form.Item>
                <Form.Item name={["address", "postal_code"]} label="Postal Code">
                  <Input />
                </Form.Item>

                <Form.Item
                  name="allergies"
                  valuePropName="checked"
                  style={{ marginBottom: 0 }}
                >
                  <Checkbox>Allergies</Checkbox>
                </Form.Item>

                <Form.Item
                  shouldUpdate={(prev, curr) =>
                    prev.allergies !== curr.allergies
                  }
                >
                  {({ getFieldValue }) =>
                    getFieldValue("allergies") ? (
                      <Form.Item
                        name="allergies_specification"
                        label="Allergy Specifications"
                        rules={[
                          {
                            required: true,
                            message: "Please specify the allergies!",
                          },
                        ]}
                      >
                        <Input />
                      </Form.Item>
                    ) : null
                  }
                </Form.Item>
              </Card>

              <Card
                type="inner"
                title="Coach/Parent Information (Type NO if not applicable)"
                size="small"
              >
                <Form.Item
                  name="ice_contact_name"
                  label="Coach/Parent Name"
                  rules={[
                    {
                      required: true,
                      message: "Please input your coach/parent name!",
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  name="relationship_to_ice"
                  label="Relationship to Contestant"
                  rules={[
                    {
                      required: true,
                      message:
                        "Please input the contact's relationship to the contestant!",
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  name="ice_contact_email"
                  label="Coach/Parent Email"
                  rules={[
                    {
                      required: true,
                      message: "Please input your email address!",
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  name="academic_institution"
                  label="Academic Institution"
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  name="provincial_director"
                  label="Provincial Director's Name"
                >
                  <Input />
                </Form.Item>
              </Card>
            </Col>
          </Row>
          <Button type="primary" htmlType="submit">
            Update
          </Button>
        </Form>
      </Space>
    </Drawer>
  );
};

export default ContestantInfoDrawer;